import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserOrganization } from "@/hooks/use-user-organization";

export interface Order {
  id: string;
  sale_id: string;
  total_cameras: number;
  total_images: number;
  status: string;
  fulfillment_status: string;
  fulfillment_notes?: string;
  fulfillment_assigned_to?: string;
  fulfillment_started_at?: string;
  fulfillment_completed_at?: string;
  delivery_address?: string;
  delivery_address_id?: string | null;
  delivery_address_record?: {
    id: string;
    contact_name?: string | null;
    company_name?: string | null;
    address_line_1?: string | null;
    address_line_2?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
    phone_number?: string | null;
  } | null;
  tracking_number?: string;
  estimated_delivery_date?: string;
  shipped_at?: string;
  order_data?: any;
  customer_user_id?: string;
  organization_id?: string;
  order_type?: string;
  skip_device_allocation?: boolean;
  allocated_device_ids?: string[];
  devices_allocated_at?: string;
  created_at: string;
  updated_at: string;
  sale?: {
    id: string;
    deal_id: string;
    total_amount: number;
    status: string;
    currency: string;
    payment_due_date?: string;
    invoice_number?: string;
    invoice_sent_at?: string;
    deal?: {
      id: string;
      title: string;
      contact_id: string;
      contact?: {
        first_name: string;
        last_name: string;
        email: string;
        organization?: {
          name: string;
          relationship_status: string;
        };
      };
    };
  };
}

export const useOrders = () => {
  const { data: userOrganizationId, isLoading: isLoadingOrg } = useUserOrganization();

  return useQuery({
    queryKey: ['orders', userOrganizationId],
    queryFn: async () => {
      console.log("🔍 Fetching orders filtered by organization:", userOrganizationId);
      
      if (!userOrganizationId) {
        console.log("❌ No organization ID found, returning empty array");
        return [];
      }
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          delivery_address_record:delivery_addresses(
            id,
            contact_name,
            company_name,
            address_line_1,
            address_line_2,
            city,
            postal_code,
            country,
            phone_number
          ),
          sale:sales(
            id,
            deal_id,
            total_amount,
            status,
            currency,
            payment_due_date,
            invoice_number,
            invoice_sent_at,
            deal:crm_deals(
              id,
              title,
              contact_id,
              contact:profiles!crm_deals_contact_id_fkey(
                first_name,
                last_name,
                email,
                organization:organizations(
                  name,
                  relationship_status
                )
              )
            )
          )
        `)
        .eq('organization_id', userOrganizationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("❌ Error fetching orders:", error);
        throw error;
      }
      
      console.log(`📊 Fetched ${data?.length || 0} orders for organization ${userOrganizationId}:`, data?.map(order => ({
        id: order.id.slice(0, 8),
        customerUserId: order.customer_user_id,
        organizationId: order.organization_id,
        status: order.status,
        orderType: order.order_type,
        dealTitle: order.sale?.deal?.title
      })));
      
      return data as Order[];
    },
    enabled: !isLoadingOrg && !!userOrganizationId,
  });
};

export const useUpdateOrderFulfillment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      orderId, 
      fulfillmentStatus, 
      fulfillmentNotes,
      fulfillmentAssignedTo,
      trackingNumber,
      deliveryAddress 
    }: {
      orderId: string;
      fulfillmentStatus: string;
      fulfillmentNotes?: string;
      fulfillmentAssignedTo?: string;
      trackingNumber?: string;
      deliveryAddress?: string;
    }) => {
      console.log("🔄 Updating order fulfillment:", orderId, fulfillmentStatus);
      
      const updateData: any = {
        fulfillment_status: fulfillmentStatus,
        updated_at: new Date().toISOString()
      };

      if (fulfillmentNotes) updateData.fulfillment_notes = fulfillmentNotes;
      if (fulfillmentAssignedTo) updateData.fulfillment_assigned_to = fulfillmentAssignedTo;
      if (trackingNumber) updateData.tracking_number = trackingNumber;
      if (deliveryAddress) updateData.delivery_address = deliveryAddress;

      // Set timestamps based on status
      if (fulfillmentStatus === 'in_progress' && !updateData.fulfillment_started_at) {
        updateData.fulfillment_started_at = new Date().toISOString();
      }
      if (fulfillmentStatus === 'shipped') {
        updateData.shipped_at = new Date().toISOString();
      }
      if (fulfillmentStatus === 'completed') {
        updateData.fulfillment_completed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast({
        title: "Success",
        description: "Order fulfillment updated successfully",
      });
    },
    onError: (error) => {
      console.error("❌ Error updating order fulfillment:", error);
      toast({
        title: "Error",
        description: "Failed to update order fulfillment",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSaleStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      saleId, 
      status, 
      invoiceNumber,
      paymentMethodDetails 
    }: {
      saleId: string;
      status: string;
      invoiceNumber?: string;
      paymentMethodDetails?: any;
    }) => {
      console.log("💳 Updating sale status:", saleId, status);
      
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (invoiceNumber) {
        updateData.invoice_number = invoiceNumber;
        updateData.invoice_sent_at = new Date().toISOString();
      }
      if (paymentMethodDetails) {
        updateData.payment_method_details = paymentMethodDetails;
      }

      const { data, error } = await supabase
        .from('sales')
        .update(updateData)
        .eq('id', saleId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast({
        title: "Success",
        description: "Sale status updated successfully",
      });
    },
    onError: (error) => {
      console.error("❌ Error updating sale status:", error);
      toast({
        title: "Error",
        description: "Failed to update sale status",
        variant: "destructive",
      });
    },
  });
};
